import React from "react";
import classes from "../style.module.scss";

export default () => (
    <>
        <p>
            <strong>Last updated: 2026-03-30</strong>
        </p>
        <div className={classes.faq}>
            <p>
                This software licensing agreement (the &quot;Agreement&quot;) is made effective as of the date of its
                execution or acceptance, as applicable (the &quot;Effective Date&quot;), by and between, on the one
                hand, Volta Research Inc. (&quot;Volta&quot;), a corporation incorporated under the laws of Canada, and
                on the other hand, the individual or entity accessing or using the App and Software (the
                &quot;User&quot;). Volta and the User may be referred to each individually as a &quot;Party&quot;, and
                collectively as the &quot;Parties&quot;.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>1. Definitions</h3>

            <p>
                <strong>1.1 &quot;App&quot;</strong> means the web-based application through which the Software is
                accessed and used.
            </p>
            <p>
                <strong>1.2 &quot;Data&quot;</strong> means &quot;Account Data,&quot; &quot;User Data,&quot; and
                &quot;Personal Data,&quot; as such terms are defined in Volta&apos;s Privacy Policy (as amended from
                time to time and available at{" "}
                <a rel="noopener noreferrer" target="_blank" href="https://app.voltasnap.ca/resources/privacy">
                    app.voltasnap.ca/resources/privacy
                </a>
                ). Volta may update its Privacy Policy at any time in its sole discretion, and such updates shall be
                effective upon posting to the website or upon such other notice as Volta may provide.
            </p>
            <p>
                <strong>1.3 &quot;Free Tier&quot;</strong> means the limited-access, no-cost version of the Software
                made available to the User through the App.
            </p>
            <p>
                <strong>1.4 &quot;Model&quot;</strong> means a digital representation of a structure, whether a house, a
                single unit in a multi-unit residential building, or an entire multi-unit residential building, created
                or maintained within the Software, whether generated by uploading an existing file, entering building
                information manually, or duplicating an existing file.
            </p>
            <p>
                <strong>1.5 &quot;Organization&quot;</strong> means the in-App group structure that may be created and
                managed through the Software to link individual accounts of authorized users affiliated with the same
                business entity, for the purpose of enabling internal file-sharing, collaboration, and other permitted
                activities among verified accounts belonging to that entity.
            </p>
            <p>
                <strong>1.6 &quot;Paid Features&quot;</strong> means premium functionality, content, or services of the
                Software made available through the App on a pay-per-use or credit basis, including those now offered or
                later developed.
            </p>
            <p>
                <strong>1.7 &quot;Project Information&quot;</strong> means any data, inputs, files, models, results,
                confidential client or third-party information, or related information created, uploaded, or otherwise
                submitted by the User or other users of the App and Software in an Organization, in connection with a
                project or analysis conducted using the App and Software.
            </p>
            <p>
                <strong>1.8 &quot;Software&quot;</strong> means Volta&apos;s proprietary software known as &quot;Volta
                SNAP&quot;, accessible using the App, including any updates, modifications, services accessible therein,
                or enhancements provided under this Agreement.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>2. Licence</h3>
            <p>
                <strong>2.1 Free Tier License</strong>
            </p>
            <p>
                Volta grants the User a limited, non-exclusive, non-transferable, revocable license to access and use
                the Free Tier solely for the User&apos;s own use.
            </p>
            <p>
                <strong>2.2 Paid Feature License</strong>
            </p>
            <p>
                Upon payment of applicable fees or credits, Volta grants the User a non-exclusive, non-transferable
                right to access and use the specified Paid Features. Each Paid Feature is licensed per transaction.
            </p>
            <p>
                <strong>2.3 Authorized Users and Organizational Accounts</strong>
            </p>
            <p>
                The license permits use of the Software by one (1) authorized User. Users who belong to the same
                business entity may link their individual accounts through the creation of an &quot;Organization&quot;
                within the App for the purpose of facilitating file-sharing and collaboration among other verified
                accounts within that Organization. Any sharing of login credentials, or any access or use of the
                Software beyond this scope, is strictly prohibited and constitutes a material breach of this Agreement,
                unless Volta provides prior written consent.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>3. Restrictions of Licence</h3>
            <p>
                <strong>3.1 General Prohibitions</strong>
            </p>
            <p>The User shall not:</p>
            <ol type="a">
                <li>Copy, modify, or create derivative works of the Software or App;</li>
                <li>Reverse engineer, decompile, or disassemble the Software or App;</li>
                <li>
                    Rent, lease, loan, or sublicense the Software to any third party, whether or not for consideration;
                </li>
                <li>Circumvent any usage limits or payment mechanisms within the Software or App;</li>
                <li>Use any Model beyond its authorized single-structure scope, as set out in Section 3.2.</li>
            </ol>
            <p>
                <strong>3.2 Model-Specific Use Restrictions</strong>
            </p>
            <p>
                <strong>3.2.1 Authorized Scope of a Model</strong>
            </p>
            <p>
                Each Model is intended to provide energy-modelling outputs for one, and only one, structure, whether a
                house, an entire multi-residential building, or a single unit within a multi-unit residential building.
            </p>
            <p>
                A Model may only be used in connection with the one particular structure for which it was originally
                created in the Software.
            </p>
            <p>
                <strong>3.2.2 Prohibition on Re-use for Additional Structures</strong>
            </p>
            <p>
                The User shall not use any Model to represent, evaluate, or generate outputs for any structure other
                than the one specific structure for which the Model was originally created. The User shall not
                duplicate, recycle, adapt, or otherwise reuse a Model to generate outputs for any other structure.
            </p>
            <p>
                <strong>3.2.3 Iterative Alterations Permitted as Long as Not Prohibited Re-Purposing</strong>
            </p>
            <p>For clarity:</p>
            <ol type="a">
                <li>
                    the User may alter inputs, and make iterative edits, refinements, and updates to a Model in the
                    ordinary course of evaluating design options for the same structure for which the Model was created;
                    however
                </li>
                <li>
                    the User shall not do so in a way that materially alters, overwrites, or re-purposes a Model so that
                    it no longer represents the same structure for which it was originally created.
                </li>
            </ol>
            <p>
                <strong>3.2.4 Effect of Prohibited Conduct</strong>
            </p>
            <p>
                Any conduct that has the effect of duplicating, recycling, adapting, or otherwise reusing a Model to
                generate outputs for a different structure than the original structure for which the Model was
                originally created, as reasonably determined by Volta, constitutes a material breach of this Agreement.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>4. Ownership &amp; Reservation of Rights</h3>
            <p>
                <strong>4.1 No Transfer of Ownership</strong>
            </p>
            <p>
                This Agreement does not confer on the User any ownership rights to the Software or App. The Software is
                licensed, not sold, and Volta remains the sole owner of all rights, title, and interest in and to the
                App and Software including all intellectual property rights. No rights are granted except those
                expressly stated.
            </p>
            <p>
                <strong>4.2 Reservation of Rights</strong>
            </p>
            <p>
                Except for those rights explicitly granted herein in this Agreement, Volta does not grant the User any
                rights to patents, copyrights, trade secrets, trademarks, or any other rights in respect to the App or
                Software. This means the User may not use Volta&apos;s trade names, trademarks, service marks or logos
                in connection with any product or service that is not Volta&apos;s, or in any manner that is likely to
                cause confusion. Volta reserves all rights not granted under this Agreement.
            </p>
            <p>
                <strong>4.3 No Agency or Partnership</strong>
            </p>
            <p>
                This Agreement does not create or imply any relationship in agency or partnership between Volta and the
                User.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>5. Account Registration and Data</h3>
            <p>
                <strong>5.1 Account Registration Required</strong>
            </p>
            <p>
                Use of the App and Software requires registration of a user account by the User (&quot;Account
                Registration&quot;). The User is responsible for maintaining the confidentiality of login credentials
                and all activity under its account.
            </p>
            <p>
                <strong>5.2 Responsibility for Account Activity</strong>
            </p>
            <p>
                The User is responsible for all activity that occurs via the User&apos;s account, even if that activity
                is not by the User or is without the User&apos;s knowledge or consent.
            </p>
            <p>
                <strong>5.3 Restrictions on Account Use</strong>
            </p>
            <p>The User may not:</p>
            <ol type="a">
                <li>
                    share the User&apos;s account information or login credentials with any third party, whether
                    intentionally or unintentionally; or
                </li>
                <li>use another individual or entity&apos;s account.</li>
            </ol>
            <p>
                <strong>5.4 Collection of Data</strong>
            </p>
            <p>
                The User acknowledges that Volta will collect, use, and store Data in accordance with its Privacy Policy
                as of the Effective Date, which is incorporated by reference into this Agreement. Volta shall provide
                the User with prior written notice of any material changes to the Privacy Policy, and continued use of
                the Software after such notice period shall constitute acceptance of the revised Privacy Policy.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>6. User Eligibility and Account Verification</h3>
            <p>
                <strong>6.1 Intended Users</strong>
            </p>
            <p>
                The Software is designed and intended for use by qualified industry professionals, including but not
                limited to architects, engineers, HVAC designers, energy advisors, researchers, and other individuals or
                entities engaged in building design or energy-performance analysis (collectively, &quot;Eligible
                Users&quot;). The Software is not intended for general consumer or homeowner use, and Volta reserves the
                right to limit access accordingly.
            </p>
            <p>
                <strong>6.2 Unverified Accounts</strong>
            </p>
            <p>
                Upon initial Account Registration, all users will be assigned an &quot;Unverified Account&quot;.
                Unverified Accounts have restricted functionality and may create up to 10 models. Access to Paid
                Features and certain advanced functionality may be disabled until the User&apos;s eligibility has been
                verified by Volta.
            </p>
            <p>
                <strong>6.3 Verification Process</strong>
            </p>
            <p>
                Volta will review information provided during Account Registration to confirm that the User qualifies as
                an Eligible User. Verification may require the User to provide additional information, such as, without
                limitation, professional credentials, organizational affiliation, or project details. Volta may contact
                the User directly to obtain such information.
            </p>
            <p>
                <strong>6.4 Account Approval and Permissions</strong>
            </p>
            <p>
                Following verification, Volta may, at its sole discretion, upgrade the User&apos;s account to
                &quot;Verified&quot; status, thereby enabling full access to the App and Software, including Paid
                Features (subject to applicable fees). Volta retains the right to at any time, at its sole discretion,
                without liability, with or without cause, deny verification or revoke Verified status if the User&apos;s
                eligibility cannot be substantiated or if information provided is inaccurate or misleading. The User
                shall be provided with written notice in the event of such denial or revocation.
            </p>
            <p>
                <strong>6.5 No Endorsement or Liability</strong>
            </p>
            <p>
                Volta&apos;s verification process is provided solely to help ensure appropriate use of the Software and
                does not constitute any endorsement, certification, or warranty of professional qualifications. Volta
                shall have no liability arising from any delay, failure, or decision in the verification process.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>7. Licence Fee</h3>
            <p>
                <strong>7.1 Free Tier</strong>
            </p>
            <p>
                No fees apply to the Free Tier, subject to usage limits defined and implemented by Volta at its
                discretion.
            </p>
            <p>
                <strong>7.2 Pay-Per-Use Fees</strong>
            </p>
            <p>
                Fees for Paid Features are set forth within the App and Software interface or on Volta&apos;s website at{" "}
                <a rel="noopener noreferrer" target="_blank" href="https://app.voltasnap.ca/pricing">
                    app.voltasnap.ca/pricing
                </a>{" "}
                and may be updated from time to time at Volta&apos;s discretion. Volta may adjust pricing or credit
                rates upon thirty (30) days&apos; notice via in-App notification and email to the email address
                associated with the User&apos;s account. Notice shall be deemed given upon transmission. Continued use
                of the App or Software after the notice period constitutes acceptance of the revised pricing.
            </p>
            <p>
                <strong>7.3 Taxes</strong>
            </p>
            <p>Fees are exclusive of applicable taxes, which shall be borne by User at the time of payment.</p>
        </div>
        <div className={classes.faq}>
            <h3>8. Term and Termination</h3>
            <p>
                <strong>8.1 Term</strong>
            </p>
            <p>
                This Agreement begins on the Effective Date and continues for one (1) year from the Effective Date,
                after which it shall automatically renew for successive one (1) year terms unless a Party provides
                written notice of non-renewal prior to the end of the then-current term, or unless terminated earlier in
                accordance with Section 8.2 or Section 8.3.
            </p>
            <p>
                <strong>8.2 Termination by User</strong>
            </p>
            <p>The User may terminate this Agreement at any time by deleting its user account.</p>
            <p>
                <strong>8.3 Termination by Volta</strong>
            </p>
            <p>
                Volta may suspend or terminate the User&apos;s access to the App, Software, the Free Tier or Paid
                Features at any time, with or without cause, including for breach of this Agreement or misuse of the
                Software. Where termination is for cause, Volta shall, where practicable, provide the User with prior
                written notice and an opportunity to cure any breach within ten (10) business days, except in cases of
                material breach, fraud, or violation of intellectual property rights, where immediate termination may
                occur. Volta shall have no liability for any such suspension or termination except as expressly provided
                in this Agreement or as required by applicable law.
            </p>
            <p>
                <strong>8.4 Effect of Termination</strong>
            </p>
            <p>Upon termination of this Agreement:</p>
            <ol type="a">
                <li>all rights granted to the User under this Agreement automatically cease; and</li>
                <li>
                    subject to applicable consumer protection laws and the User&apos;s statutory rights, all prepaid
                    pay-per-use fees and unused credits are non-refundable.
                </li>
            </ol>
            <p>
                Termination of the Free Tier does not affect credits for Paid Features that were lawfully purchased and
                not yet used, for thirty (30) days after termination. After thirty (30) days from termination, the
                User&apos;s account will be purged. Volta shall provide written notice to the User at least fifteen (15)
                days prior to purging the User&apos;s account. Subject to applicable consumer protection laws and the
                User&apos;s statutory rights, any credits for Paid Features not used within thirty (30) days of
                termination will permanently expire and the User will have no claim to access, value, or refund for
                those unused credits, except where the User&apos;s inability to use such credits was caused by
                Volta&apos;s breach or wrongful termination.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>9. Maintenance and Support</h3>
            <p>
                <strong>9.1 Technical Support</strong>
            </p>
            <p>
                Volta may, at its discretion, provide reasonable technical support to the User in connection with the
                App and Software. Such support may include troubleshooting assistance, responses to technical inquiries,
                and guidance regarding use of the App and Software.
            </p>
            <p>
                The nature, extent, timing, and method of any support provided shall be determined solely by Volta, and
                Volta shall have no obligation to provide any particular level or duration of support.
            </p>
            <p>
                <strong>9.2 Updates</strong>
            </p>
            <p>
                Volta may release patches or updates from time to time. Volta is under no obligation to provide major
                upgrades or new versions and will do so entirely at their discretion.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>10. Warranties and Disclaimers</h3>
            <p>
                <strong>10.1 No Warranties</strong>
            </p>
            <p>
                To the maximum extent permitted by applicable law, and except as expressly stated herein, the App and
                Software (including any Paid Features) are provided &quot;as is&quot; and &quot;as available,&quot;
                without any warranty of any kind, whether express or implied, including any implied warranties of
                merchantability, fitness for a particular purpose, or non-infringement. This disclaimer does not affect
                any warranties or conditions that cannot be excluded or limited under applicable law.
            </p>
            <p>
                <strong>10.2 No Warranties for Performance or Reliability</strong>
            </p>
            <p>
                Volta makes no warranty expressed or implied regarding the fitness of the App, Software, or Paid
                Features for a particular purpose or that the App, Software, or its Paid Features will be suitable or
                appropriate for the specific requirements of the User.
            </p>
            <p>
                Volta makes no representation or warranty that the App, Software, or any Paid Feature will meet the
                specific needs, objectives, or performance requirements of the User, or that the operation of the App or
                Software will be uninterrupted or error-free. The User acknowledges that software and web applications
                may contain bugs, errors, or other limitations.
            </p>
            <p>
                <strong>10.3 User Responsible for Accuracy of Outputs</strong>
            </p>
            <p>
                The User is solely responsible for verifying the accuracy, suitability, and applicability of any
                outputs, calculations, or recommendations generated by the Software before relying on them for any
                purpose whatsoever.
            </p>
            <p>
                Volta shall have no liability for any decisions, actions, or outcomes based on such outputs, including
                (without limitation) any engineering, design, sizing, or installation decisions made by the User or by
                third parties.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>11. Limitation of Liability</h3>
            <p>
                <strong>11.1</strong>
            </p>
            <p>
                To the maximum extent permitted by applicable law, Volta will not be liable for any general, special,
                indirect, incidental or consequential damages including, but not limited to, loss of production, loss of
                profits, loss of revenue, loss of data, or business interruption suffered by the User arising out of the
                use or failure of the App, Software, or Paid Features.
            </p>
            <p>
                <strong>11.2</strong>
            </p>
            <p>
                The User shall indemnify, defend, and hold harmless Volta and its affiliates, officers, directors,
                agents, employees, third party service providers, and partners from and against any and all third-party
                claims, demands, losses, damages, liabilities, costs and expenses (including reasonable legal fees)
                arising out of or related to: (a) the User&apos;s breach of this Agreement; (b) the User&apos;s
                violation of any law or regulation; (c) the User&apos;s violation of any third-party rights, including
                intellectual property rights; or (d) any negligent or wrongful act or omission by the User in connection
                with the use of the App, Software, or Paid Features, except to the extent caused by Volta&apos;s gross
                negligence or willful misconduct.
            </p>
            <p>
                <strong>11.3</strong>
            </p>
            <p>
                To the extent Sections 11.1 and 11.2 are found inapplicable or unenforceable, and except for liability
                that cannot be limited under applicable law (including liability for gross negligence, willful
                misconduct, fraud, or breach of fundamental terms), Volta&apos;s total aggregate liability to the User
                under this Agreement shall not exceed the total fees paid by the User to Volta in the twelve (12) months
                immediately preceding the event giving rise to the claim.
            </p>
        </div>
        <div className={classes.faq}>
            <h3>12. General Provisions</h3>
            <p>
                <strong>12.1 Governing Law &amp; Jurisdiction</strong>
            </p>
            <p>
                This Agreement shall be governed by and construed in accordance with the laws of the Province of
                Ontario, and the federal laws of Canada. The Parties irrevocably attorn and submit to the exclusive
                jurisdiction of the courts of the Province of Ontario, sitting in the City of Toronto, for the
                determination of any dispute arising under or in connection with this Agreement, and waive any objection
                based on venue or forum non conveniens.
            </p>
            <p>
                <strong>12.2 Entire Agreement</strong>
            </p>
            <p>
                This Agreement constitutes the entire agreement between the Parties with respect to the subject matter
                hereof and supersedes all prior and contemporaneous agreements, understandings, negotiations, and
                discussions, whether oral or written, between the Parties regarding such subject matter. Notwithstanding
                the foregoing, this Agreement does not supersede or modify any separate written agreements between the
                Parties that are expressly identified as surviving or supplementing this Agreement, including, without
                limitation, the Privacy Policy.
            </p>
            <p>
                <strong>12.3 Amendments</strong>
            </p>
            <p>
                Volta may modify this Agreement from time to time by posting a revised version within the App or on its
                website and providing notice to the User via email and in-App notification at least thirty (30) days
                prior to the effective date of such modifications. Continued use of the App, Software, and/or Paid
                Features after the effective date of the modification constitutes acceptance. Users who do not accept
                the modifications may terminate this Agreement in accordance with Section 8.2 prior to the effective
                date without penalty.
            </p>
            <p>
                <strong>12.4 Assignment</strong>
            </p>
            <p>The User may not assign this Agreement without Volta&apos;s prior written consent.</p>
            <p>
                <strong>12.5 Severability</strong>
            </p>
            <p>
                If any provision of this Agreement is held to be invalid, illegal, or unenforceable, in whole or in
                part, by a court of competent jurisdiction, such provision shall be modified and interpreted to
                accomplish the objectives of such provision to the greatest extent possible under applicable law, and
                the remaining provisions shall continue in full force and effect. If such modification is not possible,
                the invalid, illegal, or unenforceable provision shall be severed from this Agreement, and the remaining
                provisions shall continue in full force and effect, provided that such severance does not materially
                alter the fundamental nature or economic balance of this Agreement.
            </p>
            <p>
                <strong>12.6 Notices</strong>
            </p>
            <p>
                <strong>12.6.1 Notices to User</strong>
            </p>
            <p>
                Any and all notices to the User required or permitted pursuant to this Agreement shall be delivered by
                Volta by email to the User using the contact information provided in the User&apos;s account, with a
                copy provided through the App. Notices shall be deemed received: (a) if by email, on the date of
                transmission if transmitted before 5:00 p.m. (Toronto time) on a business day, or on the next business
                day if transmitted after 5:00 p.m. or on a non-business day; and (b) if through the App, upon the
                User&apos;s next login to the App. For critical notices including termination, breach, or material
                amendments, Volta shall send notice by both email and through the App. It is the User&apos;s
                responsibility to maintain current and accurate contact information within their account to ensure
                receipt of such notices.
            </p>
            <p>
                <strong>12.6.2 Notices to Volta</strong>
            </p>
            <p>
                Any and all notices to Volta required or permitted pursuant to this Agreement are to be provided by
                email to info@voltaresearch.org, or by registered mail to the following address: Volta Research Inc.,
                1655 Dupont St., Suite 101, Toronto, ON M6P 3T1, Attention: Legal. Notices to Volta shall be deemed
                received: (a) if by email, on the date of transmission if transmitted before 5:00 p.m. (Toronto time) on
                a business day, or on the next business day if transmitted after 5:00 p.m. or on a non-business day; and
                (b) if by mail or courier, upon receipt as confirmed by the delivery service.
            </p>
            <p>
                <strong>12.7 Acceptance</strong>
            </p>
            <p>
                All terms, conditions and obligations of this Agreement will be deemed to be accepted by the User as of
                the Effective Date.
            </p>
            <p>
                <strong>12.8 Force Majeure</strong>
            </p>
            <p>
                Volta will be free of liability to the User where Volta is prevented from performing its obligations
                under this Agreement, in whole or in part, due to Force Majeure Events. A &quot;Force Majeure
                Event&quot; means an event beyond the reasonable control of Volta, including but not limited to
                earthquake, typhoon, flood, fire, war, epidemic, pandemic, governmental action, labor disputes, or any
                other unforeseen and uncontrollable event, provided that Volta: (a) provides prompt written notice to
                the User of the Force Majeure Event; (b) uses commercially reasonable efforts to mitigate the effects of
                the Force Majeure Event and resume performance; and (c) resumes performance as soon as reasonably
                practicable after the Force Majeure Event ceases.
            </p>
            <p>
                <strong>12.9 Headings &amp; Interpretation</strong>
            </p>
            <p>
                Headings are inserted in this Agreement for the convenience of the Parties only and are not to be
                considered when interpreting this Agreement. Words in the singular mean and include the plural and vice
                versa. Words in the neutral gender include the masculine gender and the feminine gender and vice versa.
                Unless the context otherwise requires: (a) &quot;including&quot; and &quot;includes&quot; mean
                &quot;including without limitation&quot; and &quot;includes without limitation&quot;; (b) &quot;or&quot;
                is not exclusive; (c) references to Sections, paragraphs, or Schedules are to sections, paragraphs, or
                schedules of this Agreement; (d) &quot;business day&quot; means a day other than Saturday, Sunday, or a
                statutory holiday in the Province of Ontario; and (e) all monetary amounts are in Canadian dollars
                unless otherwise specified.
            </p>
            <p>
                <strong>12.10 Terms Binding on Successors and Assigns</strong>
            </p>
            <p>
                This Agreement and the terms and conditions contained herein apply to and are binding upon the
                Parties&apos; permitted successors and assigns, subject to the assignment restrictions set forth in this
                Agreement.
            </p>
        </div>
    </>
);
